// HOW TO ADD A NEW ROUTE
//
// 1. Create a route file, e.g. routes/$projectId/reports/index.tsx:
//
//    import { createRoute } from '@tanstack/react-router'
//    import { projectRoute } from '../route'
//
//    export const reportsRoute = createRoute({
//      getParentRoute: () => projectRoute,
//      path: '/reports/',
//      component: ReportsPage,
//    })
//
// 2. Import it in this file (router.ts):
//
//    import { reportsRoute } from './routes/$projectId/reports/index'
//
// 3. Add it to the parent's children array:
//
//    const projectTree = projectRoute.addChildren([
//      ...existing,
//      reportsRoute,  // ← add here
//    ])

import { createRouter } from '@tanstack/react-router'
import { RouteErrorPage } from './components/route-error'
import { handleCaughtRenderError } from './lib/client-error-report'

// Root
import { rootRoute } from './routes/__root'

// Top-level routes (children of rootRoute)
import { indexRoute } from './routes/index'
import { authRoute } from './routes/auth/route'
import { settingsRoute } from './routes/settings/route'

// Admin routes
import { adminRoute } from './routes/admin/route'
import { adminIndexRoute } from './routes/admin/index'
import { adminOrgDetailRoute } from './routes/admin/organizations/$orgId/route'
import { adminToolsRoute } from './routes/admin/tools/index'
import { adminBackupsRoute } from './routes/admin/backups/index'

// Project layout + index
import { projectRoute } from './routes/$projectId/route'
import { projectIndexRoute } from './routes/$projectId/index'

// Project child routes — Chat
import { chatIndexRoute } from './routes/$projectId/chat/index'
import { chatDetailRoute } from './routes/$projectId/chat/$chatId/route'
import { chatsRoute } from './routes/$projectId/chats'

// Project child routes — Write
import { writeIndexRoute } from './routes/$projectId/write/index'
import { writeDetailRoute } from './routes/$projectId/write/$docId/route'

// Project child routes — Tasks
import { tasksRoute } from './routes/$projectId/tasks/index'

// Project child routes — Inbox
import { inboxRoute } from './routes/$projectId/inbox/index'
import { inboxRulesRoute } from './routes/$projectId/inbox/rules'

// Project child routes — Draw
import { drawIndexRoute } from './routes/$projectId/draw/index'
import { drawDetailRoute } from './routes/$projectId/draw/$drawingId/route'

// Project child routes — Meetings
import { meetRoute } from './routes/$projectId/meet/$code/route'
import { calendarIndexRoute } from './routes/$projectId/calendar/index'
import { legacyMeetingsRedirectRoute } from './routes/$projectId/calendar/legacy-redirect'
import { meetingDetailRoute } from './routes/$projectId/calendar/$meetingId/route'

// Project child routes — Members redirect
import { membersRedirectRoute } from './routes/$projectId/members/index'

// Old settings pages → the settings dialog (`?settings=<section>`, board h39z)
import { projectSettingsRoute } from './routes/$projectId/settings/route'

// --- Wire the route tree ---

const projectTree = projectRoute.addChildren([
  projectIndexRoute,
  chatIndexRoute,
  chatDetailRoute,
  chatsRoute,
  writeIndexRoute,
  writeDetailRoute,
  tasksRoute,
  inboxRoute,
  inboxRulesRoute,
  drawIndexRoute,
  drawDetailRoute,
  calendarIndexRoute,
  legacyMeetingsRedirectRoute,
  meetingDetailRoute,
  meetRoute,
  membersRedirectRoute,
  projectSettingsRoute,
])

const adminTree = adminRoute.addChildren([
  adminIndexRoute,
  adminOrgDetailRoute,
  adminToolsRoute,
  adminBackupsRoute,
])

const routeTree = rootRoute.addChildren([
  indexRoute,
  authRoute,
  settingsRoute,
  adminTree,
  projectTree,
])

// --- Create and export router ---

// A view that throws gets the real error page in place of TanStack's bare
// "Something went wrong!" (board uvu5); the root and the project layout set
// the full-screen variant themselves. Every catch is logged and reported.
export const router = createRouter({
  routeTree,
  defaultErrorComponent: RouteErrorPage,
  defaultOnCatch: (error, info) => {
    handleCaughtRenderError(error, info)
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
